/*
 * Copyright (c) 2023, tuanchauict
 */

@file:Suppress("FunctionName")

package mono.html.modal.compose

import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import kotlinx.browser.localStorage
import mono.browser.manager.BrowserManager
import org.w3c.dom.get
import org.w3c.dom.set
import mono.html.modal.TooltipPosition
import mono.html.modal.tooltip
import mono.ui.compose.components.Icons
import mono.ui.compose.ext.classes
import mono.ui.compose.ext.onConsumeClick
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element

private typealias ProjectManagementAction = (ProjectManagementActionItem) -> Unit
private typealias ProjectSelectAction = (ProjectItem, isRemoved: Boolean) -> Unit

class ProjectItem(
    val id: String,
    val name: String,
    val folderPath: String = "",
    val isOpening: Boolean,
)

fun showRecentProjectsModal(
    projectItems: List<ProjectItem>,
    onManagementAction: ProjectManagementAction,
    onProjectSelect: ProjectSelectAction
) {
    NoBackgroundModal(
        attrs = {
            classes("recent-project-modal")
        }
    ) {
        ModalContent(onManagementAction, projectItems, onProjectSelect, dismiss)
    }
}

@Composable
private fun ModalContent(
    onManagementAction: ProjectManagementAction,
    projects: List<ProjectItem>,
    onProjectSelect: ProjectSelectAction,
    onDismiss: () -> Unit
) {
    Div(
        attrs = {
            classes("container")
        }
    ) {
        val filter = remember { mutableStateOf("") }
        val requestingRemoveProjectId = remember { mutableStateOf("") }

        FilterInput {
            filter.value = it
            requestingRemoveProjectId.value = ""
        }
        ProjectManagementSection(filter.value.isNotEmpty()) {
            onManagementAction(it)
            onDismiss()
        }
        ProjectList(filter.value, projects, requestingRemoveProjectId.value) {
            when (it) {
                is Action.Open -> {
                    if (it.withNewTab) {
                        BrowserManager.openInNewTab(it.project.id)
                    } else {
                        onProjectSelect(it.project, false)
                    }

                    onDismiss()
                }

                is Action.RemoveConfirm -> {
                    onProjectSelect(it.project, true)
                    onDismiss()
                }

                is Action.RequestRemove -> requestingRemoveProjectId.value = it.project.id
                Action.SuspendRemove -> requestingRemoveProjectId.value = ""
            }
        }
    }
}

@Composable
private fun FilterInput(onChange: (String) -> Unit) {
    Div(
        attrs = {
            classes("filter-container")
        }
    ) {
        Input(type = InputType.Text) {
            placeholder("Filter by name")

            onInput { onChange(it.value) }

            ref {
                it.focus()
                onDispose { }
            }
        }
    }
}

@Composable
private fun ProjectList(
    filter: String,
    projects: List<ProjectItem>,
    requestingRemoveProjectId: String,
    onAction: (Action) -> Unit
) {
    val filteredProjects = if (filter.isNotEmpty()) {
        projects
            .filter {
                it.name.contains(filter, ignoreCase = true) ||
                    it.folderPath.contains(filter, ignoreCase = true)
            }
            .sortedBy { it.name }
    } else {
        projects
    }

    if (filteredProjects.isEmpty()) {
        Div(attrs = { classes("no-item") }) {
            Span { Text("Not found") }
        }
        return
    }

    // Group by folder path so the user has a sense of structure
    // without needing a tree widget. Root drawings (empty path) come
    // first; remaining folders are listed alphabetically.
    val grouped = filteredProjects.groupBy { it.folderPath }
    val orderedFolders = buildList {
        if (grouped.containsKey("")) add("")
        addAll(grouped.keys.filter { it.isNotEmpty() }.sorted())
    }

    // Track the EXPANDED set (inverse of collapsed) so brand-new
    // folders the user has never touched default to collapsed. Persisted
    // to localStorage so the per-device preference survives reloads.
    val expanded = remember { mutableStateOf(loadExpandedFolders()) }

    // When the user is actively filtering, expand everything regardless
    // of stored state so matches inside collapsed folders are visible.
    val isFiltering = filter.isNotEmpty()

    Div(attrs = { classes("list") }) {
        for (folder in orderedFolders) {
            val isExpanded = isFiltering || folder.isEmpty() || folder in expanded.value
            if (folder.isNotEmpty()) {
                FolderHeader(
                    path = folder,
                    isCollapsed = !isExpanded,
                    isToggleable = !isFiltering,
                ) {
                    val next = expanded.value.toMutableSet().apply {
                        if (folder in this) remove(folder) else add(folder)
                    }
                    expanded.value = next
                    saveExpandedFolders(next)
                }
            }
            if (isExpanded) {
                for (project in grouped.getValue(folder)) {
                    val isRemoveConfirming = project.id == requestingRemoveProjectId
                    ProjectContent(project, isRemoveConfirming, onAction)
                }
            }
        }
    }
}

private const val EXPANDED_FOLDERS_KEY = "pms.ui.expanded-folders"

private fun loadExpandedFolders(): Set<String> {
    val raw = localStorage[EXPANDED_FOLDERS_KEY] ?: return emptySet()
    return raw.split('\n').filter { it.isNotEmpty() }.toSet()
}

private fun saveExpandedFolders(folders: Set<String>) {
    if (folders.isEmpty()) {
        localStorage.removeItem(EXPANDED_FOLDERS_KEY)
    } else {
        localStorage[EXPANDED_FOLDERS_KEY] = folders.joinToString("\n")
    }
}

@Composable
private fun FolderHeader(
    path: String,
    isCollapsed: Boolean,
    isToggleable: Boolean,
    onToggle: () -> Unit
) {
    Div(
        attrs = {
            classes(
                "folder-header",
                "collapsible" to isToggleable,
                "collapsed" to isCollapsed
            )
            if (isToggleable) {
                onConsumeClick { onToggle() }
            }
        }
    ) {
        Span(attrs = { classes("folder-toggle") }) { Icons.ChevronDown(10) }
        Icon {
            if (isCollapsed) Icons.Folder() else Icons.FolderOpen()
        }
        Span(attrs = { classes("name") }) { Text(path) }
    }
}

@Composable
private fun ProjectContent(
    item: ProjectItem,
    isRemoveConfirming: Boolean,
    onAction: (Action) -> Unit
) {
    Div(
        attrs = {
            classes(
                "item",
                "removing" to isRemoveConfirming,
                "normal" to !isRemoveConfirming
            )
            if (!isRemoveConfirming) {
                onConsumeClick { onAction(Action.Open(item, withNewTab = false)) }
            }
        }
    ) {
        if (isRemoveConfirming) {
            DeleteConfirmName(item.name)
        } else {
            ProjectName(item.name, item.isOpening)
        }
        Actions(isRemoveConfirming, item, onAction)
    }
}

@Composable
private fun DeleteConfirmName(name: String) {
    Div(attrs = { classes("name-container", "delete-confirm") }) {
        Icon { Icons.Warning() }
        Span(attrs = { classes("name") }) {
            Text("""Delete "$name"?""")
        }
    }
}

@Composable
private fun ProjectName(name: String, isOpening: Boolean) {
    Div(attrs = { classes("name-container") }) {
        Icon {
            if (isOpening) Icons.FolderOpen() else Icons.Folder()
        }
        Span(attrs = { classes("name") }) {
            Text(name)
        }
    }
}

@Composable
private fun Icon(icon: ContentBuilder<Element>) {
    Span(attrs = { classes("icon") }) { icon() }
}

@Composable
private fun Actions(
    isRemoveConfirming: Boolean,
    item: ProjectItem,
    onAction: (Action) -> Unit
) {
    Div(attrs = { classes("actions") }) {
        if (!isRemoveConfirming) {
            ActionOpenInNewTab(item, onAction)
            ActionRemoveProjectRequest(item, onAction)
        } else {
            ActionRemoveProjectConfirm(item, onAction)
        }
    }
}

@Composable
private fun ActionOpenInNewTab(project: ProjectItem, onAction: (Action) -> Unit) {
    Div(
        attrs = {
            classes("action")
            tooltip("Open in new tab", TooltipPosition.TOP)

            onConsumeClick {
                onAction(Action.Open(project, withNewTab = true))
            }
        }
    ) {
        Icons.OpenInNewTab(12)
    }
}

@Composable
private fun ActionRemoveProjectRequest(project: ProjectItem, onAction: (Action) -> Unit) {
    Div(
        attrs = {
            classes("action")
            tooltip("Delete", TooltipPosition.TOP)
            onConsumeClick { onAction(Action.RequestRemove(project)) }
        }
    ) {
        Icons.Remove(12)
    }
}

@Composable
private fun ActionRemoveProjectConfirm(item: ProjectItem, onAction: (Action) -> Unit) {
    Div(
        attrs = {
            classes("action")
            tooltip("Confirm", TooltipPosition.TOP)
            onConsumeClick {
                onAction(Action.RemoveConfirm(item))
            }
        }
    ) {
        Icons.Remove(12)
    }
    Div(
        attrs = {
            classes("action")
            tooltip("Cancel", TooltipPosition.TOP)
            onConsumeClick {
                onAction(Action.SuspendRemove)
            }
        }
    ) {
        Icons.Cross(12)
    }
}

private sealed class Action {
    data class Open(val project: ProjectItem, val withNewTab: Boolean) : Action()
    data class RequestRemove(val project: ProjectItem) : Action()
    data class RemoveConfirm(val project: ProjectItem) : Action()
    object SuspendRemove : Action()
}
